import React, { useState, useEffect } from 'react';
import { callPublicApi, callApi } from '../api';
import './YonetimSayfasi.css'; 
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    BarChart, Bar, Cell, AreaChart, Area 
} from 'recharts';
import { 
    ShieldCheck, Mail, BarChart3, TrendingUp, RotateCw, Users, 
    AlertTriangle, Server, Clock, Globe, Shield, Activity,
    ChevronRight, ExternalLink, Zap
} from 'lucide-react';

function SystemSettings() {
    const [settings, setSettings] = useState({
        EMAIL_VERIFICATION_ENABLED: '0'
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [analytics, setAnalytics] = useState({
        daily: [],
        topTargets: [],
        topErrors: [],
        exploits: [],
        members: []
    });

    useEffect(() => {
        fetchSettings();
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const data = await callApi('admin/analytics', 'GET');
            console.log("Analytics raw data:", data);
            if (data && !data.error) {
                const grouped = (data.daily || []).reduce((acc, curr) => {
                    if (!curr || !curr.date) return acc;
                    if (!acc[curr.date]) acc[curr.date] = { date: curr.date, PAGE_VIEW: 0, CLICK: 0, EXPLOIT: 0 };
                    
                    const safeType = (curr.type || '').toUpperCase();
                    // Map to expected Recharts dataKeys
                    if (safeType.includes('VIEW')) acc[curr.date]['PAGE_VIEW'] = curr.count;
                    else if (safeType.includes('CLICK')) acc[curr.date]['CLICK'] = curr.count;
                    else if (safeType.includes('EXPLOIT') || safeType.includes('ATTACK')) acc[curr.date]['EXPLOIT'] = curr.count;
                    else acc[curr.date][safeType] = curr.count;
                    
                    return acc;
                }, {});
                setAnalytics({ 
                    daily: Object.values(grouped), 
                    topTargets: data.topTargets || [], 
                    topErrors: data.topErrors || [], 
                    exploits: data.exploits || [],
                    members: data.members || []
                });
            }
        } catch (err) {
            console.error("Analiz verileri yüklenemedi:", err);
        }
    };

    const handleBlockIP = async (ip) => {
        if (!window.confirm(`${ip} adresini engellemek istediğinize emin misiniz?`)) return;
        try {
            const data = await callApi('admin/block-ip', 'POST', { ip, reason: "Panel üzerinden manuel engelleme" });
            if (data.success) {
                setMessage(`${ip} engellendi.`);
                setTimeout(() => setMessage(''), 3000);
                fetchAnalytics();
            }
        } catch (err) {
            alert("Engelleme hatası: " + err.message);
        }
    };

    const fetchSettings = async () => {
        try {
            const data = await callApi('settings', 'GET');
            if (data) {
                setSettings(prev => ({ ...prev, ...data }));
            }
        } catch (err) {
            console.error("Ayarlar yüklenemedi:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (key, currentValue) => {
        const newValue = String(currentValue) === '1' ? '0' : '1';
        try {
            const data = await callApi('settings', 'PUT', { key, value: newValue });
            if (data.success) {
                setSettings(prev => ({ ...prev, [key]: newValue }));
                setMessage('Ayar başarıyla güncellendi.');
                setTimeout(() => setMessage(''), 3000);
            }
        } catch (err) {
            alert("Hata: " + err.message);
        }
    };

    if (loading) return (
        <div className="loader" style={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#f8fafc' }}>
            <div className="animate-spin" style={{ width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%' }}></div>
            <p style={{ marginTop: '20px', color: '#64748b', fontWeight: '600' }}>Sistem Modülleri Hazırlanıyor...</p>
        </div>
    );

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="glass-effect" style={{ padding: '12px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', border: '1px solid rgba(255,255,255,0.4)' }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#1e293b' }}>{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', marginBottom: '4px' }}>
                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: entry.color }}></div>
                            <span style={{ color: '#64748b' }}>{entry.name}:</span>
                            <span style={{ fontWeight: '700', color: '#1e293b' }}>{entry.value}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="ayarlar-container" style={{ background: '#f8fafc', padding: '2rem', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                
                {/* HEADER SECTION */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#3b82f6', fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
                            <Shield size={16} />
                            Güvenlik Duvarı & Sistem
                        </div>
                        <h1 style={{ margin: 0, fontSize: '2.2rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em' }}>
                            Sistem ve Güvenlik Yönetimi
                        </h1>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div className="glass-effect" style={{ padding: '0.75rem 1.25rem', borderRadius: '14px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 12px #10b981' }}></div>
                            <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b' }}>Sistem Çevrimiçi</span>
                        </div>
                        <button 
                            onClick={fetchAnalytics}
                            className="premium-card"
                            style={{ 
                                display: 'flex', alignItems: 'center', gap: '8px', padding: '0.75rem 1.5rem', 
                                background: '#3b82f6', color: 'white', border: 'none', cursor: 'pointer', 
                                fontWeight: '700', fontSize: '0.9rem'
                            }}
                        >
                            <RotateCw size={18} /> Verileri Yenile
                        </button>
                    </div>
                </div>
                
                {/* QUICK SETTINGS ROW */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
                    
                    {/* E-POSTA VERIFICATION */}
                    <div className="premium-card" style={{ padding: '1.5rem', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', top: '-20px', right: '-20px', color: '#3b82f6', opacity: 0.05 }}>
                            <Mail size={120} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
                            <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '12px', color: '#3b82f6' }}>
                                <Mail size={24} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: '700' }}>E-posta Doğrulama</h3>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Brevo OTP altyapısını yönetin</p>
                            </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                            <div>
                                <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Sistem Durumu</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? '#10b981' : '#ef4444' }}></div>
                                    <span style={{ fontWeight: '800', color: String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? '#10b981' : '#ef4444', fontSize: '1rem' }}>
                                        {String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? 'AKTİF' : 'DEVRE DIŞI'}
                                    </span>
                                </div>
                            </div>
                            <button 
                                onClick={() => handleToggle('EMAIL_VERIFICATION_ENABLED', settings.EMAIL_VERIFICATION_ENABLED)}
                                style={{ 
                                    padding: '12px 24px', borderRadius: '12px', border: 'none', 
                                    background: String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? '#fee2e2' : '#dcfce7', 
                                    color: String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? '#ef4444' : '#10b981', 
                                    cursor: 'pointer', fontWeight: '800', transition: '0.2s',
                                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                                }}
                            >
                                {String(settings.EMAIL_VERIFICATION_ENABLED) === '1' ? 'Sistemi Durdur' : 'Aktif Et'}
                            </button>
                        </div>
                    </div>

                    {/* SERVER INFO */}
                    <div className="premium-card" style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
                            <div style={{ background: '#f5f3ff', padding: '10px', borderRadius: '12px', color: '#7c3aed' }}>
                                <Server size={24} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: '700' }}>Sunucu Parametreleri</h3>
                                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Limitler ve konfigürasyon</p>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', marginBottom: '4px' }}>
                                    <Activity size={14} />
                                    <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>Günlük Limit</span>
                                </div>
                                <span style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b' }}>300 E-posta</span>
                            </div>
                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', marginBottom: '4px' }}>
                                    <Clock size={14} />
                                    <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>OTP Ömrü</span>
                                </div>
                                <span style={{ fontSize: '1.25rem', fontWeight: '800', color: '#1e293b' }}>5 Dakika</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ANALYTICS SECTION TITLE */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '1.5rem' }}>
                    <h2 style={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: '800', margin: 0 }}>
                        Aktivite Analitiği
                    </h2>
                    <span style={{ color: '#64748b', fontSize: '0.9rem', fontWeight: '500' }}>Canlı sistem verileri ve etkileşim trendleri</span>
                </div>
                
                {/* CHARTS ROW */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    
                    {/* TREND CHART */}
                    <div className="premium-card" style={{ gridColumn: 'span 8', padding: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <TrendingUp size={20} color="#3b82f6" />
                                <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b', fontWeight: '700' }}>Haftalık Etkileşim Trendi</h3>
                            </div>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '0.75rem', fontWeight: '600' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }}></div> Ziyaret</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></div> Tıklama</div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></div> Saldırı</div>
                            </div>
                        </div>
                        <div style={{ width: '100%', height: 350 }}>
                            <ResponsiveContainer>
                                <AreaChart data={analytics.daily}>
                                    <defs>
                                        <linearGradient id="colorZiyaret" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorTıklama" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 11}} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area type="monotone" dataKey="PAGE_VIEW" name="Ziyaret" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorZiyaret)" />
                                    <Area type="monotone" dataKey="CLICK" name="Tıklama" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorTıklama)" />
                                    <Line type="smooth" dataKey="EXPLOIT" name="Saldırı" stroke="#ef4444" strokeWidth={4} dot={{r: 4, fill: '#ef4444'}} activeDot={{r: 6}} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* TOP TARGETS */}
                    <div className="premium-card" style={{ gridColumn: 'span 4', padding: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2rem' }}>
                            <BarChart3 size={20} color="#3b82f6" />
                            <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b', fontWeight: '700' }}>Popüler Sayfalar</h3>
                        </div>
                        <div style={{ width: '100%', height: 350 }}>
                            <ResponsiveContainer>
                                <BarChart data={analytics.topTargets} layout="vertical" margin={{left: -20, right: 20}}>
                                    <XAxis type="number" hide />
                                    <YAxis dataKey="target" type="category" width={100} style={{fontSize: '11px', fill: '#64748b', fontWeight: '500'}} axisLine={false} tickLine={false} />
                                    <Tooltip cursor={{fill: 'rgba(241, 245, 249, 0.5)'}} contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}} />
                                    <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={24}>
                                        {analytics.topTargets.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#94a3b8'} fillOpacity={1 - (index * 0.15)} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* LIVE MEMBERS TABLE */}
                    <div className="premium-card" style={{ gridColumn: 'span 12', padding: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ background: '#dcfce7', padding: '8px', borderRadius: '10px', color: '#10b981' }}>
                                    <Users size={20} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: '700' }}>Aktif Oturumlar</h3>
                            </div>
                            <span className="badge badge-info" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', animation: 'pulse 2s infinite' }}></div>
                                Canlı Veri
                            </span>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table className="modern-table">
                                <thead>
                                    <tr>
                                        <th>Üye Kimliği</th>
                                        <th>Son Görülme</th>
                                        <th>Gezindiği Yol</th>
                                        <th style={{ textAlign: 'center' }}>İşlem Adedi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.members && analytics.members.length > 0 ? analytics.members.map((m, idx) => (
                                        <tr key={idx}>
                                            <td style={{ fontWeight: '800', color: '#3b82f6', fontFamily: "'JetBrains Mono', 'Fira Code', monospace", fontSize: '0.95rem' }}>
                                                {m.userCode}
                                            </td>
                                            <td style={{ color: '#1e293b', fontWeight: '500' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Clock size={14} color="#94a3b8" />
                                                    {new Date(m.lastSeen).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{new Date(m.lastSeen).toLocaleDateString('tr-TR')}</span>
                                                </div>
                                            </td>
                                            <td style={{ color: '#64748b', fontSize: '0.9rem' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <Globe size={14} color="#cbd5e1" />
                                                    {m.lastPath}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className="badge badge-success" style={{ minWidth: '80px', display: 'inline-block' }}>
                                                    {m.activityCount} İşlem
                                                </span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="4" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Aktif üye oturumu bulunmuyor.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* SECURITY EXPLOITS TABLE */}
                    <div className="premium-card" style={{ gridColumn: 'span 12', padding: '1.5rem', border: '1px solid #fee2e2' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ background: '#fef2f2', padding: '8px', borderRadius: '10px', color: '#ef4444' }}>
                                    <AlertTriangle size={20} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#991b1b', fontWeight: '800' }}>Tespit Edilen Tehditlar</h3>
                            </div>
                            <div className="badge badge-danger" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <ShieldCheck size={14} /> Güvenlik Duvarı Aktif
                            </div>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                                <thead>
                                    <tr style={{ textAlign: 'left', color: '#991b1b', fontSize: '0.75rem', fontWeight: '800', textTransform: 'uppercase' }}>
                                        <th style={{ padding: '0 15px' }}>Zaman Damgası</th>
                                        <th style={{ padding: '0 15px' }}>İstemci IP</th>
                                        <th style={{ padding: '0 15px' }}>Hedef Yol</th>
                                        <th style={{ padding: '0 15px' }}>Tespit Edilen 패턴</th>
                                        <th style={{ padding: '0 15px', textAlign: 'center' }}>Aksiyon</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {analytics.exploits && analytics.exploits.length > 0 ? analytics.exploits.map(ex => (
                                        <tr key={ex.id} style={{ background: '#fff1f2', transition: '0.2s' }}>
                                            <td style={{ padding: '14px 15px', borderRadius: '12px 0 0 12px', fontSize: '0.8rem', color: '#991b1b' }}>
                                                {new Date(ex.createdAt).toLocaleString('tr-TR')}
                                            </td>
                                            <td style={{ padding: '14px 15px', fontWeight: '800', color: '#1e293b', fontFamily: 'monospace' }}>
                                                {ex.ip}
                                            </td>
                                            <td style={{ padding: '14px 15px', color: '#b91c1c', fontSize: '0.85rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {ex.path}
                                            </td>
                                            <td style={{ padding: '14px 15px' }}>
                                                <span style={{ fontSize: '0.7rem', background: '#ffffff', border: '1px solid #fecaca', color: '#b91c1c', padding: '4px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                                    {ex.metadata}
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 15px', borderRadius: '0 12px 12px 0', textAlign: 'center' }}>
                                                <button 
                                                    onClick={() => handleBlockIP(ex.ip)}
                                                    className="premium-card"
                                                    style={{ 
                                                        background: '#ef4444', color: 'white', border: 'none', 
                                                        padding: '8px 16px', borderRadius: '10px', 
                                                        cursor: 'pointer', fontSize: '0.75rem', fontWeight: '800',
                                                        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
                                                    }}
                                                >
                                                    IP ENGELLE
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: '4rem', color: '#10b981', fontWeight: '700' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                                                <Zap size={32} />
                                                Tehdit tespit edilmedi. Sistem güvenli.
                                            </div>
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* TOAST MESSAGE */}
            {message && (
                <div style={{ 
                    position: 'fixed', bottom: '2rem', right: '2rem', 
                    background: '#0f172a', color: 'white', padding: '1rem 2rem', 
                    borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)', 
                    display: 'flex', alignItems: 'center', gap: '12px',
                    animation: 'slideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', 
                    zIndex: 1000, border: '1px solid rgba(255,255,255,0.1)'
                }}>
                    <Zap size={18} color="#f59e0b" fill="#f59e0b" />
                    <span style={{ fontWeight: '600' }}>{message}</span>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(100px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                @keyframes pulse {
                    0% { transform: scale(0.95); opacity: 0.5; }
                    50% { transform: scale(1.05); opacity: 1; }
                    100% { transform: scale(0.95); opacity: 0.5; }
                }
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default SystemSettings;

